// components/OperationDashboard/QuickStatsGrid.js
import React from "react";
import { Box, useTheme, Skeleton, Grid } from "@mui/material";
import {
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { QK } from "../../base/qk";
import { getDashboardStats } from "../../api/services/dashboard";
import QuickStatCard from "./QuickStatCard";

const QuickStatsGrid = () => {
  const theme = useTheme();

  const { data: stats, isLoading } = useQuery(
    [QK.dashboardStats],
    () => getDashboardStats(),
    {
      refetchInterval: 30000,
    }
  );

  const SkeletonCard = () => (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={60} height={40} sx={{ mt: 0.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="rounded" width={40} height={16} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: "12px" }} />
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
          gap: 2.5,
          mb: 3.5,
        }}
      >
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </Box>
    );
  }

  const statsData = [
    {
      title: "Active Visits Today",
      value: stats?.active_visits_today || 0,
      icon: <HomeIcon />,
      color: theme.palette.primary.main,
      subtitle: `${stats?.total_scheduled_today || 0} scheduled today`,
      trend: `${stats?.completion_rate || 0}% complete`,
    },
    {
      title: "Visits Completed",
      value: stats?.completed_visits_today || 0,
      icon: <CheckCircleIcon />,
      color: theme.palette.success.main,
      subtitle: `${stats?.completion_rate || 0}% completion rate`,
      trend: `↑ ${stats?.weekly_completion_rate || 0}% weekly`,
    },
    {
      title: "Pending Visits",
      value: stats?.pending_visits || 0,
      icon: <ScheduleIcon />,
      color: theme.palette.warning.main,
      subtitle: `${stats?.overdue_visits || 0} overdue`,
      trend: `⚠ ${stats?.overdue_visits || 0}`,
    },
    {
      title: "Staff on Duty",
      value: stats?.staff_on_duty || 0,
      icon: <PeopleIcon />,
      color: theme.palette.info.main,
      subtitle: `${stats?.available_staff || 0} available`,
      trend: `+${stats?.available_staff || 0}`,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
        gap: 2.5,
        mb: 3.5,
      }}
    >
      {statsData.map((stat, index) => (
        <QuickStatCard
          key={index}
          title={stat.title}
          value={stat.value}
          icon={stat.icon}
          color={stat.color}
          subtitle={stat.subtitle}
          trend={stat.trend}
        />
      ))}
    </Box>
  );
};

export default QuickStatsGrid;